/**
 * Logger estruturado do Bigwa
 *
 * Níveis: info, warn, error
 * Em produção (bigwa.app): apenas error aparece no console
 * Em staging/dev: tudo aparece
 *
 * Prepara integração com Sentry (já instalado):
 * - error → Sentry.captureException automaticamente
 * - warn/info → apenas console
 */

import * as Sentry from '@sentry/react';

const isProduction = typeof window !== 'undefined' &&
  window.location.hostname === 'bigwa.app';

function formatMessage(module: string, message: string): string {
  return `[${module}] ${message}`;
}

export const logger = {
  info(module: string, message: string, data?: unknown) {
    if (!isProduction) {
      console.log(formatMessage(module, message), data ?? '');
    }
  },

  warn(module: string, message: string, data?: unknown) {
    if (!isProduction) {
      console.warn(formatMessage(module, message), data ?? '');
    }
  },

  error(module: string, messageOrError?: string | unknown, error?: unknown) {
    // Suporta dois formatos:
    // Estruturado: logger.error('Module', 'message', error?)
    // Legacy:      logger.error('descriptive message', error?)
    if (typeof messageOrError === 'string') {
      const message = messageOrError;
      console.error(formatMessage(module, message), error ?? '');

      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: { module },
          extra: { message },
        });
      } else if (error) {
        Sentry.captureMessage(formatMessage(module, message), {
          level: 'error',
          extra: { error },
        });
      } else {
        Sentry.captureMessage(formatMessage(module, message), {
          level: 'error',
        });
      }
    } else {
      // Legacy: primeiro argumento é a mensagem descritiva, segundo é o erro
      const err = messageOrError;
      console.error(module, err ?? '');

      if (err instanceof Error) {
        Sentry.captureException(err, {
          extra: { context: module },
        });
      } else if (err) {
        Sentry.captureMessage(module, {
          level: 'error',
          extra: { error: err },
        });
      } else {
        Sentry.captureMessage(module, {
          level: 'error',
        });
      }
    }
  },
};

export default logger;
